// Which rows the panel shows — the state filter and the search, both run over the rows
// already on screen. Nothing here is a figure the server keeps: the bar and the counts
// are its summary; a number column's total is only the sum of the rows being looked at.

import { formatDate } from '../DateText';
import { groupKeyOf } from './listGroups';

const norm = (s) => String(s || '').toLowerCase();

// `groupKey` (with the list's `grouping`, listGroups.groupingOf) narrows to one group;
// the three filters work together.
export const visibleItems = (items, { stateId, query, groupKey, grouping }) => {
  const q = norm(query).trim();
  return items.filter((it) => (!stateId || it.state_id === stateId)
    && (!groupKey || !grouping || groupKeyOf(it, grouping) === groupKey)
    && (!q || [it.name, it.note, ...Object.values(it.cells)].some((v) => norm(v).includes(q))));
};

export const columnTotal = (items, columnId) => {
  let sum = 0;
  for (const it of items) {
    const n = Number(String(it.cells[columnId] ?? '').replace(/,/g, ''));
    if (Number.isFinite(n)) sum += n;
  }
  return sum;
};

// A row's secondary line on a phone: its column values, in the list's order. A bare
// number or date says nothing without its column's name, so those carry it.
export const secondaryLine = (item, columns) => columns
  .filter((c) => item.cells[c.id])
  .map((c) => {
    const v = item.cells[c.id];
    if (c.kind === 'date') return `${c.label} ${formatDate(v)}`;
    return c.kind === 'number' ? `${c.label} ${v}` : v;
  })
  .join(' · ');

// Rows copied from a spreadsheet or any list and pasted into the add box: one line per
// row, cells split by tabs — the first is the name, the rest fill the list's columns in
// order. Nothing is uploaded and there is no import screen: it is typing, many lines at
// once. Empty lines are skipped; at most 500 rows per paste, as the server takes.
export const parsePaste = (text, columns) => String(text || '')
  .split(/\r?\n/)
  .map((line) => line.split('\t').map((c) => c.trim()))
  .filter((cells) => cells[0])
  .slice(0, 500)
  .map(([name, ...rest]) => ({
    name: name.slice(0, 160),
    cells: Object.fromEntries(columns.map((c, i) => [c.id, (rest[i] || '').slice(0, 500)]).filter(([, v]) => v)),
  }));

// Does this paste hold more than one value? A single plain word pastes as typing does.
export const isMultiPaste = (text) => /[\n\t]/.test(String(text || '').trim());
